/**
 * HTTP-backed implementation of {@link DeclarationSubmissionPort} (TG-4, m1-declaration-next-steps),
 * over the real, published route this feature's own TG-2/TG-3 built:
 * `POST /api/portal/submission` (`src/api/functions/submission.py`'s `submit_renewal_http`).
 *
 * The real endpoint is session-bound: the renewal, the caller's role and the server-side
 * completeness backstop (ADR-20261022-yyjf) are all resolved from the validated session claim, not
 * from the request body — so this adapter posts exactly `{representativeName, agreed}`, never
 * `renewalId`, `capability` or `sections` (those three exist on {@link DeclarationSubmissionRequest}
 * only for the stub's own client-side pre-check, ADR-20261015-bzyr's self-authored port shape — this
 * file reads the two NEW fields that request carries and nothing else).
 *
 * Refusal mapping (`src/api/functions/submission.py`'s own documented check order — role, then body,
 * then completeness, then the write):
 *   - 403 `role_not_permitted_to_submit` -> Failed, taxonomyCode `role_not_permitted_to_submit`.
 *   - 422 `invalid_declaration` -> Failed, taxonomyCode `invalid_declaration`, `fieldErrors` carried.
 *   - 422 `incomplete_sections` -> Failed, taxonomyCode `incomplete_sections`, `incompleteSectionIds` carried.
 *   - 409 `renewal_submitted` (ADR-20261022-ptps's shared submission-lock gate, reachable here on a
 *     write that raced a concurrent submission) -> Failed, taxonomyCode `renewal_submitted`.
 *   - 503 `submission_unavailable` -> Failed, taxonomyCode `submission_unavailable` (the server has
 *     already answered definitively that the write did not happen; this is never Indeterminate).
 *   - 200 -> Succeeded.
 *   - A thrown/rejected fetch (ADR-20260821-kp45/FIND-35's own network-drop trigger, the same one
 *     every other HTTP-backed write port in this directory maps the same way) -> Indeterminate: the
 *     one case this adapter cannot rule out the write having landed.
 *   - Any other response shape -> Failed, with no taxonomyCode (App.tsx's own `classifySubmissionFailure`
 *     already has to tolerate "no code at all" for the stub's own 403/422, so this is not a new case
 *     a caller must separately learn to handle).
 *
 * ADR-20261018-1skz: gated by `whenSessionTokenHeld`, exactly like the other three HTTP ports in
 * this directory — no request fires while no session token is held.
 */
import { authenticatedFetch } from "../../portal-auth/authenticated-fetch";
import type { WriteOutcome } from "../outcome";
import type {
  DeclarationFieldError,
  DeclarationSubmissionFailureDetail,
  DeclarationSubmissionPort,
  DeclarationSubmissionRequest,
  DeclarationSubmissionResult,
} from "../declaration-submission-port";
import { whenSessionTokenHeld } from "./session-gate";

const SUBMISSION_ROUTE = "/api/portal/submission";

interface WireFieldError {
  field?: unknown;
  reason?: unknown;
}

function toFieldErrors(raw: unknown): readonly DeclarationFieldError[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  return raw
    .filter((entry): entry is WireFieldError => typeof entry === "object" && entry !== null)
    .map((entry) => ({
      field: typeof entry.field === "string" ? entry.field : "",
      reason: typeof entry.reason === "string" ? entry.reason : "",
    }));
}

function toIncompleteSectionIds(raw: unknown): readonly string[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  return raw.filter((entry): entry is string => typeof entry === "string");
}

interface FailureExtras {
  taxonomyCode?: string | undefined;
  fieldErrors?: readonly DeclarationFieldError[] | undefined;
  incompleteSectionIds?: readonly string[] | undefined;
}

/** Builds the Failed outcome's `detail`, omitting any key whose value is `undefined` entirely
 * (never assigning `undefined` explicitly) — `exactOptionalPropertyTypes` (tsconfig) forbids
 * assigning `undefined` to an optional property, only omitting it. */
function failed(httpStatus: number, message: string, extra: FailureExtras = {}): WriteOutcome<DeclarationSubmissionResult> {
  const detail: DeclarationSubmissionFailureDetail = { httpStatus, message };
  if (extra.taxonomyCode !== undefined) {
    detail.taxonomyCode = extra.taxonomyCode;
  }
  if (extra.fieldErrors !== undefined) {
    detail.fieldErrors = extra.fieldErrors;
  }
  if (extra.incompleteSectionIds !== undefined) {
    detail.incompleteSectionIds = extra.incompleteSectionIds;
  }
  return { status: "Failed", detail };
}

function indeterminate(message: string): WriteOutcome<DeclarationSubmissionResult> {
  return { status: "Indeterminate", detail: { httpStatus: 0, message } };
}

async function performSubmit(
  representativeName: string,
  agreed: boolean,
): Promise<WriteOutcome<DeclarationSubmissionResult>> {
  let response: Response;
  try {
    response = await authenticatedFetch(SUBMISSION_ROUTE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ representativeName, agreed }),
    });
  } catch (error) {
    // A dropped network connection -- did the server apply this submission or not? Unknown
    // (ADR-20260821-kp45), never Failed.
    return indeterminate(error instanceof Error ? error.message : String(error));
  }

  const body: unknown = await response.json().catch(() => null);
  const bodyObject = typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
  const code = typeof bodyObject.code === "string" ? bodyObject.code : undefined;
  const message = typeof bodyObject.message === "string" ? bodyObject.message : `POST ${SUBMISSION_ROUTE} returned ${response.status}.`;

  if (response.status === 200) {
    const submittedAt = typeof bodyObject.submittedAt === "string" ? bodyObject.submittedAt : new Date().toISOString();
    return { status: "Succeeded", value: { renewalId: "", submittedAt } };
  }

  if (response.status === 422) {
    return failed(422, message, {
      taxonomyCode: code,
      fieldErrors: toFieldErrors(bodyObject.fieldErrors),
      incompleteSectionIds: toIncompleteSectionIds(bodyObject.incompleteSectionIds),
    });
  }

  return failed(response.status, message, { taxonomyCode: code });
}

export function createHttpDeclarationSubmissionPort(): DeclarationSubmissionPort & { isSampleData: false } {
  return {
    isSampleData: false,

    async submit({ renewalId, representativeName, agreed }: DeclarationSubmissionRequest) {
      const outcome = await whenSessionTokenHeld(() => performSubmit(representativeName ?? "", agreed === true));
      if (outcome.status !== "Succeeded") {
        return outcome;
      }
      return { status: "Succeeded", value: { ...outcome.value, renewalId } };
    },
  };
}
